<template>
    <div class="modal-card card">
        <form @submit.prevent="onSubmit()">
            <div class="card-header">
                <!-- Title -->
                <h4 class="card-header-title">
                    {{ isEdit ? "Edit Entitlement Discount" : "Add Entitlement Discount" }}
                </h4>
                <!-- Close -->
                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="card-body">
                <div v-if="error" class="alert alert-danger" role="alert">
                    {{ error }}
                </div>

                <InputField label="Client" type="text" :modelValue="cid" :editable="false" />

                <InputField v-if="isEdit" label="Entitlement" type="text" :modelValue="editEntitlementLabel" :editable="false" />
                <InputField v-else label="Entitlement" type="select" v-model="entitlementId" :editable="!loadingOptions"
                    :options="entitlementOptions" :helpText="entitlementHelpText"
                    :validate="[{ type: 'required', errMsg: 'select an entitlement' }]" />

                <InputField label="Discount Type" name="discountType" type="radio" v-model="discountType" :editable="true"
                    :options="discountTypeOptions" />

                <InputField v-if="discountType === 'value'" label="Free Usage per Month" type="number" v-model="discountValue"
                    :editable="true" :helpText="valueHelpText"
                    :validate="[{ type: 'required', errMsg: 'free usage is required' }]" />
                <InputField v-else label="Discount Percentage" type="number" v-model="discountPercentage" :editable="true"
                    helpText="Percentage of the consumption that is waived."
                    :validate="[{ type: 'required', errMsg: 'percentage is required' }, { type: 'minMax', minValue: 0, maxValue: 100 }]" />

                <InputField label="Effective Date" type="calendar" v-model="effectiveDate" :editable="true"
                    :inputConfig="eff_config" :validate="[{ type: 'required', errMsg: 'effective date is required' }]" />

                <InputField label="End Date" type="calendar" v-model="endDate" :editable="true" :inputConfig="end_config"
                    :validate="[{ type: 'required', errMsg: 'end date is required' }]" />
            </div>
            <div class="card-footer">
                <div class="text-end">
                    <button class="btn btn-primary" :disabled="btnDisable || saving" type="submit">
                        {{ isEdit ? "Update" : "Add" }}
                    </button>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
import InputField from "../InputField.vue";
import { mapActions } from "vuex";
import dayjs from "dayjs";
import { formatNumber } from "../../utils/commonFunction";
import {
    isSubEntitlement,
    subEntitlementLabel,
    usageInUnit,
    findOverlappingDiscount,
} from "../../utils/entitlementDiscounts";
export default {
    components: { InputField },
    name: "EntitlementDiscount",
    props: {
        modalData: {
            type: [Object, Array, String],
        },
    },
    data() {
        const discount = this.modalData && this.modalData.discount_id ? this.modalData : null;
        const monthStart = dayjs().startOf("month");
        return {
            cid: this.$route.params.id,
            discountId: discount ? discount.discount_id : null,
            entitlementId: discount ? discount.entitlement_id : null,
            discountType: discount && discount.discount_percentage != null ? "percentage" : "value",
            discountValue: discount && discount.discount_value != null ? discount.discount_value : null,
            discountPercentage: discount && discount.discount_percentage != null ? discount.discount_percentage : null,
            effectiveDate: discount ? discount.effective_date : monthStart.format("YYYY-MM-DD"),
            endDate: discount ? discount.end_date : monthStart.add(1, "year").subtract(1, "day").format("YYYY-MM-DD"),
            discountTypeOptions: [
                { label: "Free usage per month", value: "value" },
                { label: "Percentage", value: "percentage" },
            ],
            // ids of the entitlements on the client's subscribed products; null until loaded
            subscribedEntitlementIds: null,
            loadingOptions: false,
            saving: false,
            error: null,
            eff_config: {
                wrap: true,
                dateFormat: "Y-m-d",
            },
            end_config: {
                wrap: true,
                dateFormat: "Y-m-d",
            },
        };
    },
    computed: {
        isEdit() {
            return this.discountId !== null;
        },
        entitlementsById() {
            const byId = {};
            this.$store.state.admin.entitlements.forEach((item) => {
                byId[item.entitlement_id] = item;
            });
            return byId;
        },
        subEntitlements() {
            return this.$store.state.admin.entitlements.filter(isSubEntitlement);
        },
        // Sub entitlements on the client's subscribed products, whether the product maps
        // the sub entitlement itself or its base entitlement.
        subscribedSubEntitlements() {
            const ids = this.subscribedEntitlementIds;
            if (!ids) return [];
            return this.subEntitlements.filter((item) =>
                ids.has(item.entitlement_id) || ids.has(item.base_entitlement_id)
            );
        },
        // Fall back to every sub entitlement when the client's subscriptions don't
        // resolve to any, so a discount can still be given.
        showingAllEntitlements() {
            return this.subscribedEntitlementIds !== null && !this.subscribedSubEntitlements.length;
        },
        entitlementOptions() {
            const list = this.showingAllEntitlements ? this.subEntitlements : this.subscribedSubEntitlements;
            return list
                .map((item) => ({ label: subEntitlementLabel(item, this.entitlementsById), value: item.entitlement_id }))
                .sort((a, b) => a.label.localeCompare(b.label));
        },
        entitlementHelpText() {
            if (this.loadingOptions) return "Loading entitlements…";
            if (this.showingAllEntitlements)
                return "No sub-entitlements found on this client's subscriptions, so all sub-entitlements are listed.";
            return "Sub-entitlements on this client's subscribed products.";
        },
        editEntitlementLabel() {
            const entitlement = this.entitlementsById[this.entitlementId];
            return entitlement ? subEntitlementLabel(entitlement, this.entitlementsById) : `#${this.entitlementId}`;
        },
        selectedEntitlement() {
            return this.entitlementsById[this.entitlementId];
        },
        valueHelpText() {
            let text = "Raw usage count, not in the entitlement's unit. It is free every calendar month, shared by the client and its tenants.";
            if (this.discountValue > 0) {
                const inUnit = this.selectedEntitlement ? usageInUnit(this.discountValue, this.selectedEntitlement.unit) : null;
                text += `<br/><strong>${formatNumber(this.discountValue)}</strong>${inUnit ? ` (${inUnit})` : ""} free each month.`;
            }
            return text;
        },
        amountValid() {
            if (this.discountType === "percentage")
                return this.discountPercentage > 0 && this.discountPercentage <= 100;
            return this.discountValue > 0;
        },
        btnDisable() {
            return !(this.entitlementId && this.amountValid && this.effectiveDate && this.endDate && this.endDate >= this.effectiveDate);
        },
    },
    watch: {
        effectiveDate: {
            handler(newVal) {
                this.end_config.minDate = newVal ? newVal.toString() : null;
            },
            immediate: true,
        },
        endDate: {
            handler(newVal) {
                this.eff_config.maxDate = newVal ? newVal.toString() : null;
            },
            immediate: true,
        },
    },
    beforeMount() {
        this.loadEntitlementOptions();
    },
    methods: {
        ...mapActions("admin", {
            fetchEntitlements: "fetchEntitlements",
            fetchProductEntitlements: "fetchProductEntitlements",
            createEntitlementDiscount: "createEntitlementDiscount",
            updateEntitlementDiscount: "updateEntitlementDiscount",
        }),
        async loadEntitlementOptions() {
            this.loadingOptions = true;
            try {
                if (!this.$store.state.admin.entitlements.length) {
                    await this.fetchEntitlements();
                }
                if (this.isEdit) return;
                const productIds = [...new Set(this.$store.state.admin.clientProducts.map((sub) => sub.product_id))];
                // A product that fails to load only narrows the list; the fallback covers the rest.
                const results = await Promise.allSettled(productIds.map((id) => this.fetchProductEntitlements(id)));
                const ids = new Set();
                results.forEach((result) => {
                    if (result.status === "fulfilled") {
                        result.value.forEach((item) => ids.add(Number(item.entitlement_id)));
                    }
                });
                this.subscribedEntitlementIds = ids;
            } catch (error) {
                this.error = this.errorMessage(error);
            } finally {
                this.loadingOptions = false;
            }
        },
        errorMessage(error) {
            return (error.response && error.response.data && error.response.data.error) || error.message;
        },
        async onSubmit() {
            this.error = null;
            const discount = {
                entitlement_id: this.entitlementId,
                effective_date: this.effectiveDate,
                end_date: this.endDate,
            };
            // client-mgmt takes exactly one of the two
            if (this.discountType === "percentage") discount.discount_percentage = this.discountPercentage;
            else discount.discount_value = this.discountValue;

            const overlap = findOverlappingDiscount(
                { ...discount, discount_id: this.discountId },
                this.$store.state.admin.entitlementDiscounts
            );
            if (overlap) {
                this.error = `This overlaps the discount from ${overlap.effective_date} to ${overlap.end_date} on the same entitlement. Discounts on one entitlement cannot overlap.`;
                return;
            }

            this.saving = true;
            try {
                if (this.isEdit) {
                    await this.updateEntitlementDiscount({ cid: this.cid, discountId: this.discountId, discount });
                } else {
                    await this.createEntitlementDiscount({ cid: this.cid, discount });
                }
            } catch (error) {
                this.error = this.errorMessage(error);
                return;
            } finally {
                this.saving = false;
            }
            this.$store.dispatch("addToast", {
                message: `Entitlement discount ${this.isEdit ? "updated" : "added"} successfully.`,
                type: "success",
            });
            this.$store.dispatch("hideModal");
            this.$store.dispatch("admin/fetchEntitlementDiscounts", this.cid);
        },
    },
};
</script>

<style></style>
